import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Badge, Group, Paper, Select, SimpleGrid, Stack, Text, ThemeIcon, Title, Tooltip } from '@mantine/core';
import {
    IconCircleCheck,
    IconClipboardList,
    IconClock,
    IconCoin,
    IconFlag,
    IconInfoCircle,
    IconStack2,
    IconX,
} from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

import {
    getReportOverview,
    listCompletedJobs,
    listOrganizations,
    listPendingBacklog,
    type ListParams,
} from '../../api/resources';
import { EntityRef } from '../../components/EntityRef';
import { PageHeader } from '../../components/PageHeader';
import { PaginatedTable } from '../../components/PaginatedTable';
import { DEFAULT_PAGE_SIZE } from '../../hooks/useListQuery';
import type { CompletedJobRow, LocationStatus, PendingBacklogRow } from '../../types/api';

const currencyFormatter = new Intl.NumberFormat('en-NG', {
    style: 'currency',
    currency: 'NGN',
    maximumFractionDigits: 0,
});

function formatCurrency(value: number): string {
    return currencyFormatter.format(value || 0);
}

function formatDate(value: string | null): string {
    if (!value) {
        return '—';
    }
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? '—' : date.toLocaleDateString();
}

function formatDays(value: number | null): string {
    if (value === null || Number.isNaN(value)) {
        return '—';
    }
    return `${value} days`;
}

function formatVerifiedDuration(days: number | null): string {
    if (days === null || Number.isNaN(days)) {
        return '—';
    }
    if (days >= 365) {
        const years = Math.floor(days / 365);
        return years === 1 ? '1 year ago' : `${years} years ago`;
    }
    if (days >= 30) {
        const months = Math.floor(days / 30);
        return months === 1 ? '1 month ago' : `${months} months ago`;
    }
    if (days < 1) {
        return 'today';
    }
    return `${Math.round(days)} days ago`;
}

function formatDistanceMeters(meters: number | null): string {
    if (meters === null || Number.isNaN(meters)) {
        return '—';
    }
    if (meters < 1000) {
        return `${Math.round(meters)} m`;
    }
    return `${(meters / 1000).toFixed(1)} km`;
}

const LOCATION_STATUS_COLORS: Record<LocationStatus, string> = {
    close: 'green',
    within_range: 'teal',
    out_of_range: 'orange',
    extremely_far: 'red',
};

function LocationStatusBadge({ status }: { status: LocationStatus | null }) {
    const { t } = useTranslation();
    if (!status) {
        return <Text size="sm">—</Text>;
    }
    return (
        <Badge color={LOCATION_STATUS_COLORS[status]} variant="light">
            {t(`features.report.locationStatus.${status}`)}
        </Badge>
    );
}

function CounterCard({
    label,
    value,
    icon,
    color,
    tooltip,
}: {
    label: string;
    value: string;
    icon: typeof IconCoin;
    color: string;
    tooltip: string;
}) {
    const Icon = icon;
    return (
        <Paper p="lg" radius="lg" withBorder shadow="xs">
            <Group justify="space-between" align="flex-start" wrap="nowrap">
                <Stack gap={4}>
                    <Group gap={6} wrap="nowrap">
                        <Text size="sm" c="dimmed">
                            {label}
                        </Text>
                        <Tooltip label={tooltip} multiline w={260} withArrow events={{ hover: true, focus: true, touch: true }}>
                            <ThemeIcon variant="subtle" color="gray" size="xs" radius="xl">
                                <IconInfoCircle size={14} stroke={1.5} />
                            </ThemeIcon>
                        </Tooltip>
                    </Group>
                    <Title order={3}>{value}</Title>
                </Stack>
                <ThemeIcon variant="light" color={color} size="lg" radius="md">
                    <Icon size={18} stroke={1.5} />
                </ThemeIcon>
            </Group>
        </Paper>
    );
}

export function ReportPage() {
    const { t } = useTranslation();
    const [orgFilter, setOrgFilter] = useState<string | null>(null);
    const [completedPage, setCompletedPage] = useState(1);
    const [backlogPage, setBacklogPage] = useState(1);

    const orgsQ = useQuery({
        queryKey: ['organizations-all'],
        queryFn: () => listOrganizations({ page: 1, limit: 500 }),
    });

    const overviewQ = useQuery({
        queryKey: ['reporting-overview', orgFilter],
        queryFn: () => getReportOverview(orgFilter ? { organizationId: orgFilter } : {}),
    });

    const completedParams = useMemo(
        (): ListParams => ({
            page: completedPage,
            limit: DEFAULT_PAGE_SIZE,
            ...(orgFilter ? { organizationId: orgFilter } : {}),
        }),
        [completedPage, orgFilter],
    );
    const completedQ = useQuery({
        queryKey: ['reporting-completed', completedParams],
        queryFn: () => listCompletedJobs(completedParams),
    });

    const backlogParams = useMemo(
        (): ListParams => ({
            page: backlogPage,
            limit: DEFAULT_PAGE_SIZE,
            ...(orgFilter ? { organizationId: orgFilter } : {}),
        }),
        [backlogPage, orgFilter],
    );
    const backlogQ = useQuery({
        queryKey: ['reporting-backlog', backlogParams],
        queryFn: () => listPendingBacklog(backlogParams),
    });

    const counters = overviewQ.data?.counters;
    const orgOptions = (orgsQ.data?.data ?? []).map((o) => ({ value: o.id, label: o.name }));

    return (
        <Stack gap="lg">
            <PageHeader
                title={t('features.report.title')}
                description={t('features.report.description')}
                actions={
                    <Select
                        placeholder={t('common.all')}
                        clearable
                        searchable
                        data={orgOptions}
                        value={orgFilter}
                        onChange={setOrgFilter}
                        w={240}
                    />
                }
            />

            <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="md">
                <CounterCard
                    label={t('features.report.counters.totalVolume')}
                    value={String(counters?.totalVolume ?? 0)}
                    icon={IconClipboardList}
                    color="violet"
                    tooltip={t('features.report.tooltips.totalVolume')}
                />
                <CounterCard
                    label={t('features.report.counters.totalValue')}
                    value={formatCurrency(counters?.totalValue ?? 0)}
                    icon={IconCoin}
                    color="yellow"
                    tooltip={t('features.report.tooltips.totalValue')}
                />
                <CounterCard
                    label={t('features.report.counters.completed')}
                    value={String(counters?.completed ?? 0)}
                    icon={IconCircleCheck}
                    color="green"
                    tooltip={t('features.report.tooltips.completed')}
                />
                <CounterCard
                    label={t('features.report.counters.pendingBacklog')}
                    value={String(counters?.pendingBacklog ?? 0)}
                    icon={IconStack2}
                    color="blue"
                    tooltip={t('features.report.tooltips.pendingBacklog')}
                />
                <CounterCard
                    label={t('features.report.counters.rejected')}
                    value={String(counters?.rejected ?? 0)}
                    icon={IconX}
                    color="red"
                    tooltip={t('features.report.tooltips.rejected')}
                />
                <CounterCard
                    label={t('features.report.counters.flagged')}
                    value={String(counters?.flagged ?? 0)}
                    icon={IconFlag}
                    color="orange"
                    tooltip={t('features.report.tooltips.flagged')}
                />
                <CounterCard
                    label={t('features.report.counters.averageVisitTat')}
                    value={
                        counters?.averageVisitTatDays != null
                            ? formatDays(counters.averageVisitTatDays)
                            : '—'
                    }
                    icon={IconClock}
                    color="cyan"
                    tooltip={t('features.report.tooltips.averageVisitTat')}
                />
            </SimpleGrid>

            <Stack gap="sm">
                <Title order={4}>{t('features.report.completedJobs')}</Title>
                <PaginatedTable
                    rowKey={(r: CompletedJobRow) => r.visitId}
                    columns={[
                        {
                            key: 'customer',
                            header: t('features.report.columns.customer'),
                            render: (r) => (
                                <EntityRef
                                    title={r.customerName ?? t('features.report.unknownCustomer')}
                                    id={r.caseId}
                                    href={`/cases/${r.caseId}`}
                                />
                            ),
                        },
                        {
                            key: 'department',
                            header: t('features.report.columns.department'),
                            render: (r) => r.department,
                        },
                        {
                            key: 'dateAdded',
                            header: t('features.report.columns.dateAdded'),
                            render: (r) => formatDate(r.dateAdded),
                        },
                        {
                            key: 'dateVerified',
                            header: t('features.report.columns.dateVerified'),
                            render: (r) => formatDate(r.dateVerified),
                        },
                        {
                            key: 'visitTat',
                            header: t('features.report.columns.visitTat'),
                            render: (r) => formatDays(r.visitTatDays),
                        },
                        {
                            key: 'caseTat',
                            header: t('features.report.columns.caseTat'),
                            render: (r) => formatDays(r.caseTatDays),
                        },
                        {
                            key: 'verifiedDuration',
                            header: t('features.report.columns.verifiedDuration'),
                            render: (r) => formatVerifiedDuration(r.verifiedDurationDays),
                        },
                        {
                            key: 'distanceAddress',
                            header: t('features.report.columns.distanceAddress'),
                            render: (r) => formatDistanceMeters(r.distanceAddressMeters),
                        },
                        {
                            key: 'locationStatus',
                            header: t('features.report.columns.locationStatus'),
                            render: (r) => <LocationStatusBadge status={r.locationStatus} />,
                        },
                    ]}
                    rows={completedQ.data?.data ?? []}
                    total={completedQ.data?.total ?? 0}
                    page={completedPage}
                    pageSize={DEFAULT_PAGE_SIZE}
                    onPageChange={setCompletedPage}
                    isLoading={completedQ.isLoading}
                />
            </Stack>

            <Stack gap="sm">
                <Title order={4}>{t('features.report.pendingBacklogTable')}</Title>
                <PaginatedTable
                    rowKey={(r: PendingBacklogRow) => r.visitId}
                    columns={[
                        {
                            key: 'customer',
                            header: t('features.report.columns.customer'),
                            render: (r) => (
                                <EntityRef
                                    title={r.customerName ?? t('features.report.unknownCustomer')}
                                    id={r.caseId}
                                    href={`/cases/${r.caseId}`}
                                />
                            ),
                        },
                        {
                            key: 'status',
                            header: t('features.report.columns.status'),
                            render: (r) =>
                                t(`visitStatuses.${r.status}`, { defaultValue: r.status }),
                        },
                        {
                            key: 'dateAdded',
                            header: t('features.report.columns.dateAdded'),
                            render: (r) => formatDate(r.dateAdded),
                        },
                    ]}
                    rows={backlogQ.data?.data ?? []}
                    total={backlogQ.data?.total ?? 0}
                    page={backlogPage}
                    pageSize={DEFAULT_PAGE_SIZE}
                    onPageChange={setBacklogPage}
                    isLoading={backlogQ.isLoading}
                />
            </Stack>
        </Stack>
    );
}
